import React, { useState, useEffect } from 'react';
import { HTTPService } from '../../services/httpService';

const API = new HTTPService();

// BuyerPicker — the register's buyer bar. Three mutually-exclusive modes:
// walk-in (anonymous-capable) / existing customer / dealer (wholesale). Tier is
// DERIVED from the mode (dealer → wholesale), never a toggle. Emits a normalized
// buyer object via onBuyerChange; null while no valid buyer is chosen.
//
// Reuses the scoped buyer endpoints (never the open /customers, /dealers reads):
//   GET /customerInstall/:installerId
//   GET /dealerInstall/:installerId?locationId=&radius_miles=50
//
// Props:
//   installerId, locationId
//   onBuyerChange(buyer | null)
//     buyer = { kind:'walkin'|'customer'|'dealer', tier:'retail'|'wholesale',
//               id, name, email, phone, isNew, row }
//   onExistingMatch(customerRow)  — optional; fires when a customer is selected
//                                   (register uses it for the open-job combine prompt)
//   modes — optional array of mode keys to offer (default all three);
//           the pill bar is hidden when only one mode is offered
//   initialMode — optional starting mode (default 'walkin')

const TEAL = 'var(--color-brand-teal)';
const MODES = [
  { key: 'walkin', label: 'Walk-in' },
  { key: 'customer', label: 'Customer' },
  { key: 'dealer', label: 'Dealer' },
];

const BuyerPicker = ({ installerId, locationId, onBuyerChange, onExistingMatch, modes, initialMode }) => {
  const [mode, setMode] = useState(initialMode || 'walkin');
  const [walkInName, setWalkInName] = useState('');
  const [walkInContact, setWalkInContact] = useState('');
  const [customers, setCustomers] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);

  // Lazy-load the relevant list when entering customer/dealer mode.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (mode === 'customer' && customers.length === 0) {
          setLoading(true);
          const { data } = await API.get(`/customerInstall/${installerId}`);
          if (!cancelled) setCustomers(data || []);
        } else if (mode === 'dealer' && dealers.length === 0) {
          setLoading(true);
          const params = new URLSearchParams();
          if (locationId && locationId !== 'all') {
            params.set('locationId', locationId);
            params.set('radius_miles', '50');
          }
          const qs = params.toString() ? `?${params.toString()}` : '';
          const { data } = await API.get(`/dealerInstall/${installerId}${qs}`);
          if (!cancelled) setDealers(data || []);
        }
      } catch (err) {
        if (!cancelled) (mode === 'dealer' ? setDealers([]) : setCustomers([]));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, installerId, locationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const emitWalkIn = (name, contact) => {
    const isEmail = (contact || '').includes('@');
    onBuyerChange && onBuyerChange({
      kind: 'walkin', tier: 'retail', id: null,
      name: name || null,
      email: isEmail ? contact : null,
      phone: !isEmail && contact ? contact : null,
      isNew: true, row: null,
    });
  };

  const switchMode = (m) => {
    setMode(m); setSelected(null); setQuery('');
    if (m === 'walkin') emitWalkIn(walkInName, walkInContact);
    else onBuyerChange && onBuyerChange(null);
  };

  const selectCustomer = (c) => {
    setSelected(c);
    onBuyerChange && onBuyerChange({
      kind: 'customer', tier: 'retail', id: c.customer_id, name: c.customer_name,
      email: c.customer_email || null, phone: c.customer_phone_number || null, isNew: false, row: c,
    });
    onExistingMatch && onExistingMatch(c);
  };

  const selectDealer = (d) => {
    setSelected(d);
    onBuyerChange && onBuyerChange({
      kind: 'dealer', tier: 'wholesale', id: d.dealer_id, name: d.dealer_name,
      email: d.dealer_email || null, phone: d.dealer_phone_number || null, isNew: false, row: d,
    });
  };

  const list = mode === 'dealer' ? dealers : customers;
  const nameOf = (r) => (mode === 'dealer' ? r.dealer_name : r.customer_name) || '';
  const subOf = (r) => (mode === 'dealer' ? r.dealer_phone_number : r.customer_phone_number) || '';
  const idOf = (r) => (mode === 'dealer' ? r.dealer_id : r.customer_id);
  const q = query.trim().toLowerCase();
  const results = !q ? list : list.filter((r) => `${nameOf(r)} ${subOf(r)}`.toLowerCase().includes(q));

  const offeredModes = MODES.filter((m) => !modes || modes.includes(m.key));

  return (
    <div className="buyer-picker">
      {offeredModes.length > 1 && (
      <div className="d-flex" style={{ gap: 8 }}>
        {offeredModes.map((m) => {
          const active = mode === m.key;
          return (
            <button
              key={m.key}
              type="button"
              className="btn btn-sm"
              onClick={() => switchMode(m.key)}
              style={{
                borderRadius: 9999, fontWeight: active ? 600 : 500, fontSize: 13,
                background: active ? TEAL : 'var(--color-bg-card)',
                color: active ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)',
                border: active ? 'none' : '1px solid var(--color-border)',
              }}
            >
              {m.label}
            </button>
          );
        })}
      </div>
      )}

      {mode === 'walkin' && (
        <div className="d-flex mt-2" style={{ gap: 8 }}>
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Name (optional)"
            value={walkInName}
            onChange={(e) => { setWalkInName(e.target.value); emitWalkIn(e.target.value, walkInContact); }}
          />
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Phone or email for receipt (optional)"
            value={walkInContact}
            onChange={(e) => { setWalkInContact(e.target.value); emitWalkIn(walkInName, e.target.value); }}
          />
        </div>
      )}

      {(mode === 'customer' || mode === 'dealer') && (
        <div className="mt-2">
          {selected ? (
            <div className="d-flex justify-content-between align-items-center p-2" style={{ background: 'var(--color-bg-panel)', borderRadius: 8 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text-primary)' }}>{nameOf(selected)}</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                  {subOf(selected)}{mode === 'dealer' ? ' · Wholesale' : ''}
                </div>
              </div>
              <button type="button" className="btn btn-link btn-sm" onClick={() => { setSelected(null); onBuyerChange && onBuyerChange(null); }}>
                <i className="fa-sharp fa-regular fa-xmark" style={{ color: 'var(--color-text-muted)' }} />
              </button>
            </div>
          ) : (
            <>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder={mode === 'dealer' ? 'Search dealer…' : 'Customer name, phone, or email…'}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
              />
              {loading && <div className="text-muted small mt-2">Loading…</div>}
              {!loading && q && (
                <div style={{ maxHeight: 180, overflowY: 'auto' }} className="mt-1">
                  {results.length === 0 && <div className="text-muted small p-2">No matches.</div>}
                  {results.slice(0, 25).map((r) => (
                    <div
                      key={idOf(r)}
                      className="p-2"
                      style={{ cursor: 'pointer', borderBottom: '1px solid var(--color-border-light)' }}
                      onClick={() => (mode === 'dealer' ? selectDealer(r) : selectCustomer(r))}
                    >
                      <div style={{ fontSize: 14, color: 'var(--color-text-primary)' }}>{nameOf(r)}</div>
                      <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{subOf(r)}</div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default BuyerPicker;
